import { useEffect, useId, useRef, useState } from "react";
import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

// =============================================================================
// CancelTripDialog
//
// Replaces the plain confirm prompt for trip cancellation with a
// reason-capture flow. The BFF, audit log, and triplink client all
// already accept a `reason` string up to 200 chars; surfacing the
// input in the UI gets the audit trail filled in right now and sets
// dispatch up to forward useful context the moment triplink wires
// their cancel endpoint.
//
// Quick-pick chips cover the cases dispatch actually sees most of the
// time (no-show, reschedule, opt-out, duplicate). "Other" reveals the
// notes textarea and requires text before the destructive button
// unlocks — that's the only way to keep "Other" from devolving into
// a dumpster of reason-less cancellations.
// =============================================================================

const QUICK_REASONS = [
  { id: "patient_no_show", label: "Patient no-show" },
  { id: "rescheduled", label: "Rescheduled" },
  { id: "patient_declined", label: "Patient declined" },
  { id: "duplicate", label: "Duplicate booking" },
  { id: "other", label: "Other" },
] as const;

type QuickReasonId = (typeof QUICK_REASONS)[number]["id"];

interface CancelTripDialogProps {
  open: boolean;
  friendlyId: string;
  patientName: string;
  pending: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

export function CancelTripDialog({
  open,
  friendlyId,
  patientName,
  pending,
  onCancel,
  onConfirm,
}: CancelTripDialogProps) {
  const [picked, setPicked] = useState<QuickReasonId | null>(null);
  const [notes, setNotes] = useState("");
  const notesRef = useRef<HTMLTextAreaElement | null>(null);
  const notesId = useId();

  // Reset every time the dialog opens so the next cancellation starts
  // from a clean slate. (Without this, picking "Other" + typing on
  // trip A would still be visible when opening trip B's dialog.)
  useEffect(() => {
    if (open) {
      setPicked(null);
      setNotes("");
    }
  }, [open]);

  // Auto-focus the notes textarea when "Other" gets chosen so the
  // dispatcher can start typing without an extra click.
  useEffect(() => {
    if (picked === "other") {
      notesRef.current?.focus();
    }
  }, [picked]);

  const trimmedNotes = notes.trim();
  // "Other" means free-text is the *only* signal we have, so require
  // it. Any other pick can ride alone.
  const canSubmit =
    picked !== null && (picked !== "other" || trimmedNotes.length > 0);

  function handleConfirm() {
    if (!canSubmit) return;
    const label = QUICK_REASONS.find((r) => r.id === picked)?.label ?? "";
    const reason =
      picked === "other"
        ? trimmedNotes
        : trimmedNotes
          ? `${label} — ${trimmedNotes}`
          : label;
    // Trim to schema limit defensively — server validates the same
    // 200-char ceiling but a no-network failure on a too-long string
    // would be a confusing UX.
    onConfirm(reason.slice(0, 200));
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !pending) onCancel();
      }}
    >
      <DialogContent
        hideCloseButton
        structured
        className="max-w-[480px]"
        onInteractOutside={(e) => {
          if (pending) e.preventDefault();
        }}
      >
        <DialogHeader icon={AlertTriangle} tone="danger">
          <DialogTitle>Cancel trip {friendlyId}?</DialogTitle>
          <DialogDescription>
            {patientName
              ? `${patientName}'s trip will be removed from dispatch's queue. This action can't be undone.`
              : "This trip will be removed from dispatch's queue. This action can't be undone."}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="grid gap-5">
          <fieldset className="grid">
            <legend className="mb-2.5 text-sm font-semibold text-foreground/90">
              Why is this being cancelled?
            </legend>
            <div className="flex flex-wrap gap-2">
              {QUICK_REASONS.map((r) => {
                const active = picked === r.id;
                return (
                  <button
                    key={r.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setPicked(r.id)}
                    className={cn(
                      "inline-flex h-8 items-center gap-1.5 rounded-lg border px-3 text-[13px] font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      active
                        ? "border-foreground/30 bg-foreground/[0.08] text-foreground"
                        : "border-border text-foreground/75 hover:border-foreground/25 hover:bg-foreground/[0.04] hover:text-foreground",
                    )}
                  >
                    {active ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : null}
                    {r.label}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <div className="grid gap-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <label htmlFor={notesId} className="text-sm font-semibold text-foreground/90">
                Notes{" "}
                <span className="font-normal text-muted-foreground">
                  {picked === "other" ? "(required)" : "(optional)"}
                </span>
              </label>
              <span
                className={cn(
                  "text-xs tabular-nums text-muted-foreground",
                  notes.length >= 180 && "text-amber-700 dark:text-amber-300",
                )}
              >
                {notes.length}/200
              </span>
            </div>
            <textarea
              id={notesId}
              ref={notesRef}
              rows={2}
              maxLength={200}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                picked === "other"
                  ? "What happened? (e.g. patient transferred to another facility)"
                  : "Anything dispatch should know?"
              }
              className="min-h-[4.5rem] w-full resize-y rounded-xl border border-border bg-foreground/[0.03] px-3 py-2.5 text-sm text-foreground transition placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </DialogBody>

        <DialogFooter>
          <Button type="button" variant="secondary" disabled={pending} onClick={onCancel}>
            Keep trip
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!canSubmit || pending}
            onClick={handleConfirm}
          >
            {pending ? (
              <>
                <Loader2 className="h-4 w-4 motion-safe:animate-spin" aria-hidden="true" />
                Cancelling…
              </>
            ) : (
              "Cancel trip"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
